// Shared MUI sx tokens for API Security pages — aligns tabs, fields, tables,
// buttons, and icon buttons with v2 theme (Rubik via theme.typography, neutral borders, 8px radius).
import colors from "../../../../../utils/colors";
import typography from "../../../../../theme/typography";
import { wafOverviewStatNumberSx } from "../web_application/components/WAFOverviewMetricCard";
import { containedButtonSx, ghostButtonSx, outlinedButtonSx, primaryButtonSx } from "../../../../common/Button";

/** Project root typography — use variants or spread these; never set fontFamily locally. */
export { typography as asecTypography };

/** 12px helper / description copy — reduces crowded secondary text (LDCP-168). */
export const asecHelperTextSx = {
  lineHeight: "25px",
};

/** KPI cards — theme sizes when using WAFOverviewMetricCard. */
export const asecOverviewMetricCardSx = {
  "& > .MuiStack-root:first-of-type .MuiTypography-root": {
    ...typography.textSmall,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: colors.primary[300],
  },
  "& [data-overview-metric]": wafOverviewStatNumberSx,
  "& [data-hero-stat-unit]": {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "18px",
    color: colors.neutral[600],
  },
};

/** Green enabled tag — matches Schema Validation "Enabled" status pill. */
export const ASEC_ENABLED_TAG = {
  bg: "#E8FBEF",
  fg: "#0f7a42",
  bd: "#C0EED4",
};

export const asecEnabledTagChipSx = {
  height: "auto",
  minHeight: 22,
  fontSize: 12,
  fontWeight: 500,
  borderRadius: "999px",
  backgroundColor: ASEC_ENABLED_TAG.bg,
  color: ASEC_ENABLED_TAG.fg,
  border: `1px solid ${ASEC_ENABLED_TAG.bd}`,
  "& .MuiChip-label": { px: 1.125, py: 0.25, lineHeight: 1.3 },
  "& .MuiChip-icon": { ml: 0.75, color: "inherit" },
};

/** Light orange learn / observing tag. */
export const ASEC_LEARN_TAG = {
  bg: "#FFF6D0",
  fg: "#7a5b10",
  bd: "#FFE89A",
};

export const asecLearnTagChipSx = {
  height: "auto",
  minHeight: 22,
  fontSize: 12,
  fontWeight: 500,
  borderRadius: "999px",
  backgroundColor: ASEC_LEARN_TAG.bg,
  color: ASEC_LEARN_TAG.fg,
  border: `1px solid ${ASEC_LEARN_TAG.bd}`,
  "& .MuiChip-label": { px: 1.125, py: 0.25, lineHeight: 1.3 },
  "& .MuiChip-icon": { ml: 0.75, color: "inherit" },
};

/** Primary indigo tag — matches WAF Configuration "Primary" badge. */
export const ASEC_PRIMARY_TAG = {
  bg: "rgba(99, 113, 247, 0.12)",
  fg: colors.primary[300],
  bd: colors.primary[300],
};

export const asecPrimaryTagChipSx = {
  height: "auto",
  minHeight: 22,
  fontSize: 11,
  fontWeight: 500,
  borderRadius: "4px",
  backgroundColor: ASEC_PRIMARY_TAG.bg,
  color: ASEC_PRIMARY_TAG.fg,
  border: `1px solid ${ASEC_PRIMARY_TAG.bd}`,
  "& .MuiChip-label": { px: 1, py: 0.25, lineHeight: 1.3 },
  "& .MuiChip-icon": { ml: 0.75, color: "inherit" },
};

export const asecTabsSx = {
  mb: 2,
  minHeight: "inherit",
  borderBottom: `1px solid ${colors.neutral[200]}`,
  "& .MuiTabs-flexContainer": {
    border: "none",
    px: 0,
    py: 0,
    gap: "24px",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    minHeight: 40,
  },
  "& .MuiTabs-indicator": {
    height: "2px",
    backgroundColor: colors.primary[300],
    borderRadius: "2px 2px 0 0",
  },
  "& .MuiTab-root": {
    fontSize: 14,
    lineHeight: "21px",
    fontWeight: 500,
    textTransform: "none",
    color: colors.neutral[700],
    opacity: 1,
    minHeight: 40,
    minWidth: "inherit",
    px: 0,
    py: 0,
    gap: 1,
    WebkitFontSmoothing: "subpixel-antialiased",
    MozOsxFontSmoothing: "auto",
    "&.Mui-selected": {
      color: colors.primary[300],
      fontWeight: 600,
      opacity: 1,
    },
    "&:hover": {
      color: colors.neutral[900],
      opacity: 1,
    },
    "&.Mui-selected:hover": {
      color: colors.primary[300],
      opacity: 1,
    },
    "& .MuiTab-iconWrapper": {
      margin: 0,
      opacity: 1,
      "& svg": {
        display: "block",
      },
    },
  },
};

export const asecTextFieldSx = {
  // Prefer <AsecTextField /> — stacked label matches site onboarding fields.
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 14,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: colors.neutral[300] },
    "&:hover fieldset": { borderColor: colors.neutral[400] },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary[300],
      borderWidth: 1,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: 14,
    color: colors.neutral[800],
  },
  "& .MuiInputBase-input::placeholder": {
    opacity: 0.85,
  },
};

export { AsecTextField } from "./components/AsecTextField";
export { AsecField } from "./components/AsecField";

export const asecSelectSx = {
  borderRadius: "8px",
  fontSize: 14,
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[300] },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[400] },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.primary[300],
  },
};

/** 40px stacked outlined control — matches AsecTextField / GreenSelectForm in drawers. */
export const asecStackedFieldHeightSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    height: 40,
    maxHeight: 40,
    boxSizing: "border-box",
  },
};

export const asecStackedSelectSx = {
  ...asecSelectSx,
  ...asecStackedFieldHeightSx,
  "& .MuiSelect-select": {
    minHeight: "unset",
    height: "100%",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
    marginTop: 0,
  },
};

/** Inline form-row control height — pair with size="small" TextField / Select. */
export const asecFormRowControlSx = {
  // Select theme override is 48px on .MuiSelect-root — set root height when sx is spread on Select.
  height: 40,
  minHeight: 40,
  maxHeight: 40,
  boxSizing: "border-box",
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    maxHeight: 40,
    boxSizing: "border-box",
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-select": {
    py: 0,
    minHeight: "unset",
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

export const asecTablePaperSx = {
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  boxShadow: "none",
  overflow: "hidden",
};

export const asecTableHeadRowSx = {
  backgroundColor: colors.neutral[50],
};

export const asecTableHeadCellSx = {
  fontSize: 12,
  fontWeight: 600,
  color: colors.neutral[700],
  borderBottom: `1px solid ${colors.neutral[200]}`,
};

export const asecTableBodyCellSx = {
  ...typography.body1,
  color: colors.neutral[1000],
  borderBottom: `1px solid ${colors.neutral[200]}`,
};

export const asecSubPanelSx = {
  backgroundColor: colors.neutral[100],
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  p: 3,
};

export const asecSectionTitleSx = {
  ...typography.h3Bold,
  color: colors.neutral[1000],
};

export const asecFieldLabelSx = {
  ...typography.formField,
  fontWeight: 500,
  color: colors.neutral[800],
  mb: 1,
  display: "block",
};

export const asecBtnPrimary = {
  ...containedButtonSx,
  textTransform: "none",
};

export const asecBtnPrimarySm = {
  ...containedButtonSx,
  textTransform: "none",
  minHeight: 32,
  ...typography.body1,
  px: 2,
  py: 0.5,
};

/** Shared 32px card-toolbar height + startIcon spacing for sm icon buttons. */
const asecBtnIconSmHeight = {
  height: 32,
  minHeight: 32,
  maxHeight: 32,
  py: 0,
  lineHeight: "20px",
  boxSizing: "border-box",
};

const asecBtnIconSmStart = {
  "& .MuiButton-startIcon": {
    marginRight: "6px",
    marginLeft: 0,
    display: "inherit",
  },
  "& .MuiButton-startIcon > *": { fontSize: 16 },
};

/** Card-toolbar primary with icon — 32px, balanced icon/text gap. */
export const asecBtnPrimaryIconSm = {
  ...asecBtnPrimarySm,
  ...asecBtnIconSmHeight,
  ...asecBtnIconSmStart,
};

/** Inline form-row primary — matches size="small" field height (40px). */
export const asecFormPrimaryButtonSx = {
  ...primaryButtonSx,
  height: 40,
  minHeight: 40,
  maxHeight: 40,
  py: 0,
  px: 2,
  fontSize: 14,
  lineHeight: "20px",
  boxSizing: "border-box",
  "& .MuiButton-startIcon": {
    marginRight: "6px",
    marginLeft: 0,
    "& svg": { fontSize: 16 },
  },
};

/** Paired secondary action at form-row height (e.g. Graphql sidebar stack). */
export const asecFormGhostButtonSx = {
  ...ghostButtonSx,
  height: 40,
  minHeight: 40,
  px: 2,
  fontSize: 14,
  lineHeight: "20px",
  "& .MuiButton-startIcon": {
    marginRight: "6px",
    marginLeft: 0,
  },
};

export const asecBtnOutlined = {
  ...outlinedButtonSx,
  textTransform: "none",
};

export const asecBtnOutlinedSm = {
  ...outlinedButtonSx,
  textTransform: "none",
  minHeight: 32,
  ...typography.body1,
  px: 2,
  py: 0.5,
};

/** Card-toolbar outlined with icon — 32px, balanced icon/text gap. */
export const asecBtnOutlinedIconSm = {
  ...asecBtnOutlinedSm,
  ...asecBtnIconSmHeight,
  ...asecBtnIconSmStart,
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: "inherit",
  },
};

/** Destructive outlined action — red border/text, transparent fill (LDCP-172). */
export const asecBtnDangerOutlinedSm = {
  ...asecBtnOutlinedIconSm,
  color: "#A11228",
  border: "solid 1px #FBC9CF",
  backgroundColor: "transparent",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: "inherit",
  },
  "&&:hover": {
    border: "solid 1px #A11228",
    color: "#A11228",
    backgroundColor: "#FEECEE",
  },
  "&&:active": {
    border: "solid 1px #A11228",
    color: "#A11228",
    backgroundColor: "#FEECEE",
  },
  "&&.Mui-disabled": {
    color: colors.neutral[500],
    borderColor: colors.neutral[200],
    backgroundColor: "transparent",
  },
};

export const asecBtnTextSm = {
  textTransform: "none",
  fontSize: 14,
  fontWeight: 500,
  color: colors.primary[300],
  "&:hover": {
    backgroundColor: colors.primary["alpha"],
  },
};

export const asecIconButtonSx = {
  borderRadius: "8px",
  color: colors.neutral[600],
  "&:hover": {
    backgroundColor: colors.primary["alpha"],
    color: colors.primary[300],
  },
};

export const asecDialogActionsSx = {
  px: 3,
  py: 2,
  gap: 1.5,
  borderTop: `1px solid ${colors.neutral[200]}`,
};

/** Bot Configuration–style mode segmented control (Off / Log only / Enforce). */
export const ASEC_MODE_TONE_COLORS = {
  off: { idle: colors.neutral[700], active: colors.primary[300] },
  log: { idle: "#7a5b10", active: "#7a5b10" },
  warn: { idle: "#7a5b10", active: "#7a5b10" },
  info: { idle: colors.primary[500], active: colors.primary[300] },
  enforce: { idle: "#A11228", active: "#A11228" },
  danger: { idle: "#A11228", active: "#A11228" },
};

export const asecModeSegmentedTrackSx = {
  display: "flex",
  width: "100%",
  backgroundColor: colors.neutral[50],
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: "10px",
  p: "4px",
  gap: "2px",
};

export const asecToggleButtonGroupSx = {
  gap: 0.5,
  backgroundColor: colors.neutral[50],
  p: 0.5,
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "6px !important",
    mx: 0,
    "&:not(:first-of-type)": { borderRadius: "6px !important", borderLeft: 0 },
  },
};

export const asecToggleButtonSx = {
  textTransform: "none",
  fontSize: 12,
  fontWeight: 500,
  color: colors.neutral[700],
  px: 2,
  "&.Mui-selected": {
    backgroundColor: `${colors.primary[300]} !important`,
    color: "#fff",
    "& svg": {
      color: "#fff",
      stroke: "#fff",
    },
    "&:hover": { backgroundColor: `${colors.primary[400]} !important` },
  },
  "&:hover": {
    backgroundColor: "rgba(99, 113, 247, 0.08)",
  },
};

// ── Event-table primitives (aligned with WAFEvent page) ────────
//
// Shared typography + cell sizing so Posture / Anomaly / future
// security-event pages render with the same visual weight as the
// existing WAF events page. Keep these in sync with the constants
// exported from `_new/detection/WAFEvent.js` — `tableHeadSx` and
// `tableBodySx` there are the source of truth for v2.

export const asecEventTableHeadSx = {
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: 500,
  color: colors.neutral[1000],
  borderBottom: `1px solid ${colors.neutral[200]}`,
  py: 2,
};

export const asecEventTableBodySx = {
  fontSize: 14,
  lineHeight: "21px",
  color: colors.neutral[800],
};

// Mono + blue treatment for URI / identity / row-id cells —
// matches the WAF event row look (#1E40AF is the v2 accent blue).
export const asecMonoBlueSx = {
  ...typography.textSmall,
  color: "#1E40AF",
};

// Row hover background. WAF events uses primary[50]; we reuse the
// same tone so the two pages feel like the same product.
export const asecEventRowSx = {
  cursor: "pointer",
  "&:hover": { backgroundColor: colors.primary[50] },
};

/** Posture/Anomaly/API event tables — aligned with WAF Events table padding. */
export const asecFindingsTableSx = {
  "& .MuiTableCell-head": { py: 3, px: 3 },
  "& .MuiTableCell-body": { py: 2, px: 3 },
  "& .MuiTableCell-root": { borderBottom: "none" },
};

/** Alias — use for all API Security event / audit log tables. */
export const asecEventsTableSx = asecFindingsTableSx;

export const asecEventsTableContainerSx = {
  border: `solid 1px ${colors.neutral[200]}`,
  borderRadius: "8px",
  p: 2,
  overflow: "hidden",
};

// Severity → pill config. Wider input acceptance than WAF's
// `getSeverityConfig` because the posture / anomaly streams use
// lowercase labels straight from the PG check constraint
// (`critical`, `high`, `medium`, `low`, `info`) whereas the WAF
// stream uses `S1` / `S2` style risk_level. Returning the same
// `{label, color, bgColor}` shape keeps the consumer code uniform.
export function asecSeverityConfig(severity) {
  const s = (severity || "").toString().toLowerCase();
  switch (s) {
    case "s1":
    case "s1critical":
    case "critical":
      return { label: "Critical", color: "#B91C1C", bgColor: "#FEE2E2" };
    case "s2":
    case "s2high":
    case "high":
      return { label: "High", color: "#C2410C", bgColor: "#FFEDD5" };
    case "s3":
    case "s3medium":
    case "medium":
      return { label: "Medium", color: "#B45309", bgColor: "#FEF3C7" };
    case "s4":
    case "s4low":
    case "low":
      return { label: "Low", color: "#15803D", bgColor: "#DCFCE7" };
    case "info":
      return { label: "Info", color: colors.neutral[700], bgColor: colors.neutral[100] };
    default:
      return { label: severity || "—", color: colors.neutral[700], bgColor: colors.neutral[100] };
  }
}

// Finding lifecycle → status dot color. Mirrors WAF's `getStatusColor`
// in semantic terms: open = red (action needed), suppressed = yellow
// (acknowledged but deferred), resolved = green (closed).
export function asecStatusDotColor(state) {
  switch ((state || "").toLowerCase()) {
    case "resolved":  return colors.green[300];
    case "suppressed": return colors.yellow[100];
    case "open":
    default:          return colors.red[100];
  }
}

// Severity sort order — used by columns that aggregate counts by
// severity ("2 critical · 3 high · …"). Critical first so the most
// urgent stands at the front of the label.
export const asecSeverityOrder = ["critical", "high", "medium", "low", "info"];

// ── Filter bar (aligned with WAF EventFilterBar + Settings AlertsPage) ──

export const FILTER_CONTROL_HEIGHT = 36;
const brandBlue = colors.primary[300];

/** White card shell for filter rows — matches EventFilterBar.js outer box. */
export const EVENT_FILTER_BAR_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: 2,
  backgroundColor: "white",
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
};

const filterOutlinedRootSx = {
  height: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  maxHeight: FILTER_CONTROL_HEIGHT,
  borderRadius: "8px",
  fontSize: 14,
  backgroundColor: "#fff",
  boxSizing: "border-box",
  alignItems: "center",
  "& fieldset": { borderColor: colors.neutral[200] },
  "&:hover fieldset": { borderColor: colors.neutral[300] },
  "&.Mui-focused fieldset": { borderColor: brandBlue },
};

export const filterInputSx = {
  height: FILTER_CONTROL_HEIGHT,
  "& .MuiOutlinedInput-root": filterOutlinedRootSx,
  "& input.MuiOutlinedInput-input": {
    paddingTop: 0,
    paddingBottom: 0,
    height: "20px",
    lineHeight: "20px",
    boxSizing: "border-box",
  },
  "& .MuiInputAdornment-root": {
    marginRight: 0.5,
    height: "auto",
    maxHeight: FILTER_CONTROL_HEIGHT,
  },
};

export const filterSelectSx = {
  height: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  maxHeight: FILTER_CONTROL_HEIGHT,
  boxSizing: "border-box",
  "& .MuiOutlinedInput-root": filterOutlinedRootSx,
  "& .MuiSelect-select": {
    py: 0,
    pl: 1.5,
    pr: 4,
    height: `${FILTER_CONTROL_HEIGHT}px !important`,
    minHeight: `${FILTER_CONTROL_HEIGHT}px !important`,
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
    right: 8,
  },
};

export const filterRefreshSx = {
  width: FILTER_CONTROL_HEIGHT,
  height: FILTER_CONTROL_HEIGHT,
  minWidth: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  backgroundColor: "#fff",
  flexShrink: 0,
  p: 0,
  "&:hover": { backgroundColor: colors.neutral[50] },
  "&:disabled": { opacity: 0.5 },
};

/** Table / list row selection — Layerd primary indigo. */
export const asecCheckboxSx = {
  color: colors.neutral[400],
  "&.Mui-checked": { color: colors.primary[300] },
  "&.MuiCheckbox-indeterminate": { color: colors.primary[300] },
};

// ── Section / content cards ───────────────────────────────────────────

/** 8px radius — tables, filters, event panels. */
export const asecSectionCardSx = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  overflow: "hidden",
};

/** 12px radius — settings-style list tables and KPI tiles. */
export const asecContentCardSx = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  border: `1px solid ${colors.neutral[200]}`,
  overflow: "hidden",
};

/** Settings list table head/body — AlertsPage pattern. */
export const asecSettingsTableHeadSx = {
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: 500,
  color: colors.neutral[1000],
};

export const asecSettingsTableBodySx = {
  fontSize: 14,
  lineHeight: "21px",
  color: colors.neutral[800],
};
